// src/pages/Announcements.jsx (ADMIN)
import { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Stack,
  TextField,
  Typography,
  Tabs,
  Tab,
  InputAdornment,
} from "@mui/material";

import CampaignIcon from "@mui/icons-material/Campaign";
import EditIcon from "@mui/icons-material/Edit";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import SearchIcon from "@mui/icons-material/Search";

import { db } from "../firebase";
import { onValue, ref, push, set, update, remove } from "firebase/database";

// For month filter dropdown
const MONTH_OPTIONS = [
  { id: "ALL", label: "All months" },
  { id: "0", label: "January" },
  { id: "1", label: "February" },
  { id: "2", label: "March" },
  { id: "3", label: "April" },
  { id: "4", label: "May" },
  { id: "5", label: "June" },
  { id: "6", label: "July" },
  { id: "7", label: "August" },
  { id: "8", label: "September" },
  { id: "9", label: "October" },
  { id: "10", label: "November" },
  { id: "11", label: "December" },
];

const CATEGORY_META = {
  HOLIDAY: {
    label: "Holiday",
    color: "#e53935",
    bg: "rgba(229,57,53,0.12)",
  },
  POLICY: {
    label: "Policy",
    color: "#fb8c00",
    bg: "rgba(251,140,0,0.12)",
  },
  EVENT: {
    label: "Event",
    color: "#1e88e5",
    bg: "rgba(30,136,229,0.12)",
  },
  HR: {
    label: "HR Update",
    color: "#8e24aa",
    bg: "rgba(142,36,170,0.12)",
  },
  SYSTEM: {
    label: "System",
    color: "#3949ab",
    bg: "rgba(57,73,171,0.12)",
  },
  GENERAL: {
    label: "General",
    color: "#546e7a",
    bg: "rgba(84,110,122,0.12)",
  },
};

const todayISO = () => new Date().toISOString().slice(0, 10);

const emptyForm = {
  id: null,
  title: "",
  summary: "",
  body: "",
  date: todayISO(),        // main date
  validUntil: todayISO(),  // REQUIRED till date
  category: "GENERAL",
  status: "DRAFT",
};

// Tabs: Draft / Published / Completed
const TABS = [
  { id: "DRAFT", label: "Draft" },
  { id: "PUBLISHED", label: "Published" },
  { id: "COMPLETED", label: "Completed" },
];

export default function Announcements() {
  const [items, setItems] = useState([]);

  const [activeTab, setActiveTab] = useState("DRAFT");
  const [search, setSearch] = useState("");
  const [monthFilter, setMonthFilter] = useState("ALL");

  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);

  // delete popup
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [pendingDelete, setPendingDelete] = useState(null);
  const [deletingId, setDeletingId] = useState(null);

  /* --------- Load announcements from RTDB --------- */
  useEffect(() => {
    const r = ref(db, "announcements");
    const unsub = onValue(r, (snap) => {
      const val = snap.val() || {};
      const arr = Object.entries(val).map(([id, v]) => ({
        id,
        ...(v || {}),
      }));

      arr.sort((a, b) => (b.date || 0) - (a.date || 0)); // latest first
      setItems(arr);
    });

    return () => unsub();
  }, []);

  /* --------- Classification: draft / published / completed --------- */

  const classify = (item) => {
    const isPublished = item.status === "PUBLISHED";
    if (!isPublished) return "DRAFT";

    const now = Date.now();
    const mainDate = item.date || 0;
    const validUntil = item.validUntil || mainDate;

    if (validUntil && validUntil < now) return "COMPLETED";
    return "PUBLISHED";
  };

  const counts = useMemo(() => {
    let draft = 0;
    let published = 0;
    let completed = 0;
    items.forEach((it) => {
      const c = classify(it);
      if (c === "DRAFT") draft += 1;
      else if (c === "PUBLISHED") published += 1;
      else if (c === "COMPLETED") completed += 1;
    });
    return { draft, published, completed };
  }, [items]);

  /* --------- Filtering for list --------- */

  const visibleItems = useMemo(() => {
    const q = search.trim().toLowerCase();
    const month = monthFilter;

    return items.filter((it) => {
      // tab classification
      if (classify(it) !== activeTab) return false;

      // search by title
      if (q && !(it.title || "").toLowerCase().includes(q)) return false;

      // filter by month (using main date)
      if (month !== "ALL") {
        if (!it.date) return false;
        const d = new Date(it.date);
        const m = String(d.getMonth());
        if (m !== month) return false;
      }

      return true;
    });
  }, [items, activeTab, search, monthFilter]);

  /* --------- Helpers --------- */

  const fmtDate = (ms) =>
    ms
      ? new Date(ms).toLocaleDateString(undefined, {
          day: "2-digit",
          month: "short",
          year: "numeric",
        })
      : "No date";

  const openNewDialog = () => {
    // new announcements always start as draft, with till date prefilled to today
    const today = todayISO();
    setForm({
      ...emptyForm,
      date: today,
      validUntil: today,
      status: "DRAFT",
    });
    setDialogOpen(true);
  };

  const openEditDialog = (item) => {
    const baseDate = new Date(item.date || Date.now()).toISOString().slice(0, 10);
    const till =
      item.validUntil != null
        ? new Date(item.validUntil).toISOString().slice(0, 10)
        : baseDate; // if somehow null in DB, default to main date

    setForm({
      id: item.id,
      title: item.title || "",
      summary: item.summary || "",
      body: item.body || "",
      date: baseDate,
      validUntil: till,
      category: item.category || "GENERAL",
      status: item.status || "DRAFT",
    });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!form.title.trim()) return;
    if (!form.validUntil) return; // till date is required

    setSaving(true);
    const now = Date.now();
    const ts = new Date(form.date).getTime() || now;
    const validUntilTs = new Date(form.validUntil).getTime() || ts;

    try {
      if (form.id) {
        // update existing
        const payload = {
          ...form,
          date: ts,
          validUntil: validUntilTs,
          updatedAt: now,
        };
        await update(ref(db, `announcements/${form.id}`), payload);
      } else {
        // create new – ALWAYS draft
        const newRef = push(ref(db, "announcements"));
        const id = newRef.key;
        const payload = {
          ...form,
          id,
          status: "DRAFT",
          date: ts,
          validUntil: validUntilTs,
          createdAt: now,
          updatedAt: now,
        };
        await set(newRef, payload);
      }
      setDialogOpen(false);
      setForm(emptyForm);
    } catch (e) {
      console.error("Save announcement error:", e);
    } finally {
      setSaving(false);
    }
  };

  const requestDelete = (item) => {
    setPendingDelete(item);
    setDeleteDialogOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!pendingDelete) return;
    const id = pendingDelete.id;
    setDeletingId(id);
    try {
      await remove(ref(db, `announcements/${id}`));
    } catch (e) {
      console.error("Delete announcement error:", e);
    } finally {
      setDeletingId(null);
      setPendingDelete(null);
      setDeleteDialogOpen(false);
    }
  };

  const handleClearFilters = () => {
    setSearch("");
    setMonthFilter("ALL");
    setActiveTab("DRAFT");
  };

  /* --------- UI --------- */

  const validUntilError = !form.validUntil;

  return (
    <Box>
      {/* Header */}
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        sx={{ mb: 3 }}
      >
        <Stack direction="row" spacing={1.5} alignItems="center">
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: 2,
              bgcolor: "primary.main",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "#fff",
              boxShadow: "0 6px 18px rgba(37,99,235,0.4)",
            }}
          >
            <CampaignIcon fontSize="small" />
          </Box>
          <Box>
            <Typography variant="h5" fontWeight={700}>
              Announcements
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Share holidays, policy updates and important messages with
              employees.
            </Typography>
          </Box>
        </Stack>

        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={openNewDialog}
          sx={{ borderRadius: 999, px: 2.5 }}
        >
          New announcement
        </Button>
      </Stack>

      {/* Top filter row (summary + search + month + clear) */}
      <Stack
        direction="row"
        spacing={2}
        alignItems="center"
        flexWrap="wrap"
        sx={{ mb: 2 }}
      >
        {/* Summary pills */}
        <Stack direction="row" spacing={1.5} flexWrap="wrap">
          <SummaryPill
            label="Draft"
            count={counts.draft}
            color="#6366F1"
            bg="rgba(99,102,241,0.08)"
          />
          <SummaryPill
            label="Published"
            count={counts.published}
            color="#16A34A"
            bg="rgba(22,163,74,0.08)"
          />
          <SummaryPill
            label="Completed"
            count={counts.completed}
            color="#64748B"
            bg="rgba(148,163,184,0.18)"
          />
        </Stack>

        <Box sx={{ flexGrow: 1 }} />

        {/* Search */}
        <TextField
          size="small"
          placeholder="Search by title…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          sx={{
            minWidth: 220,
            maxWidth: 280,
            "& .MuiOutlinedInput-root": { borderRadius: 999 },
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          }}
        />

        {/* Filter by month */}
        <TextField
          size="small"
          select
          value={monthFilter}
          onChange={(e) => setMonthFilter(e.target.value)}
          SelectProps={{ native: true }}
          sx={{
            minWidth: 160,
            "& .MuiOutlinedInput-root": { borderRadius: 999 },
          }}
        >
          {MONTH_OPTIONS.map((m) => (
            <option key={m.id} value={m.id}>
              {m.label}
            </option>
          ))}
        </TextField>

        {/* Clear */}
        <Button
          variant="text"
          size="small"
          onClick={handleClearFilters}
          sx={{ textTransform: "none" }}
        >
          Clear
        </Button>
      </Stack>

      {/* Tabs + list container */}
      <Card sx={{ borderRadius: 3 }}>
        <CardContent sx={{ p: 0 }}>
          {/* Tabs */}
          <Tabs
            value={activeTab}
            onChange={(_, v) => setActiveTab(v)}
            variant="scrollable"
            scrollButtons="auto"
            sx={{
              px: 3,
              pt: 1.5,
              "& .MuiTab-root": {
                textTransform: "none",
                fontWeight: 500,
                minWidth: "auto",
                mr: 3,
              },
            }}
          >
            <Tab value="DRAFT" label={`Draft (${counts.draft})`} />
            <Tab
              value="PUBLISHED"
              label={`Published (${counts.published})`}
            />
            <Tab
              value="COMPLETED"
              label={`Completed (${counts.completed})`}
            />
          </Tabs>
          <Divider />

          {/* List / empty state */}
          <Box sx={{ p: 3 }}>
            {visibleItems.length === 0 ? (
              <Card
                sx={{
                  borderRadius: 3,
                  py: 6,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexDirection: "column",
                  bgcolor: "grey.50",
                  border: "1px dashed rgba(148,163,184,0.7)",
                }}
              >
                <Box
                  sx={{
                    width: 48,
                    height: 48,
                    borderRadius: "50%",
                    bgcolor: "rgba(148,163,184,0.18)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    mb: 1.5,
                  }}
                >
                  <InfoOutlinedIcon sx={{ color: "text.secondary" }} />
                </Box>
                <Typography fontWeight={600} sx={{ mb: 0.5 }}>
                  No announcements in this section.
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Use “New announcement” to create one, or adjust filters above.
                </Typography>
              </Card>
            ) : (
              visibleItems.map((a) => {
                const meta =
                  CATEGORY_META[a.category] || CATEGORY_META.GENERAL;

                return (
                  <Card
                    key={a.id}
                    sx={{
                      mb: 1.5,
                      borderRadius: 3,
                      boxShadow: "0 4px 18px rgba(15,23,42,0.06)",
                      border: "1px solid rgba(226,232,240,0.9)",
                    }}
                  >
                    <CardContent sx={{ py: 1.75 }}>
                      <Stack
                        direction="row"
                        alignItems="flex-start"
                        spacing={2}
                        justifyContent="space-between"
                      >
                        {/* Left: icon + text */}
                        <Stack direction="row" spacing={1.75} sx={{ flex: 1 }}>
                          <Box
                            sx={{
                              width: 40,
                              height: 40,
                              borderRadius: "50%",
                              bgcolor: meta.bg,
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              flexShrink: 0,
                            }}
                          >
                            <CampaignIcon
                              sx={{ fontSize: 20, color: meta.color }}
                            />
                          </Box>

                          <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Stack
                              direction="row"
                              alignItems="center"
                              justifyContent="space-between"
                              spacing={1}
                              sx={{ mb: 0.25 }}
                            >
                              <Typography
                                sx={{
                                  fontWeight: 600,
                                  whiteSpace: "nowrap",
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                }}
                              >
                                {a.title || "Untitled announcement"}
                              </Typography>
                              <Chip
                                size="small"
                                label={meta.label}
                                sx={{
                                  borderRadius: 999,
                                  bgcolor: meta.bg,
                                  color: meta.color,
                                  fontSize: 11,
                                  height: 22,
                                }}
                              />
                            </Stack>

                            {a.summary && (
                              <Typography
                                variant="body2"
                                color="text.secondary"
                                sx={{
                                  mb: 0.75,
                                  display: "-webkit-box",
                                  WebkitLineClamp: 2,
                                  WebkitBoxOrient: "vertical",
                                  overflow: "hidden",
                                }}
                              >
                                {a.summary}
                              </Typography>
                            )}

                            <Stack
                              direction="row"
                              spacing={1.5}
                              alignItems="center"
                              flexWrap="wrap"
                            >
                              <Stack
                                direction="row"
                                spacing={0.75}
                                alignItems="center"
                              >
                                <CalendarMonthIcon
                                  sx={{
                                    fontSize: 16,
                                    color: "text.secondary",
                                  }}
                                />
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                >
                                  {fmtDate(a.date)}
                                </Typography>
                              </Stack>

                              {a.validUntil && (
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                >
                                  • Visible until {fmtDate(a.validUntil)}
                                </Typography>
                              )}

                              <Chip
                                size="small"
                                label={
                                  classify(a) === "COMPLETED"
                                    ? "Completed"
                                    : a.status === "PUBLISHED"
                                    ? "Published"
                                    : "Draft"
                                }
                                sx={{
                                  height: 22,
                                  fontSize: 11,
                                  borderRadius: 999,
                                  bgcolor:
                                    classify(a) === "COMPLETED"
                                      ? "rgba(148,163,184,0.18)"
                                      : a.status === "PUBLISHED"
                                      ? "rgba(22,163,74,0.08)"
                                      : "rgba(129,140,248,0.12)",
                                  color:
                                    classify(a) === "COMPLETED"
                                      ? "rgba(55,65,81,0.9)"
                                      : a.status === "PUBLISHED"
                                      ? "#15803d"
                                      : "#4f46e5",
                                }}
                              />
                            </Stack>
                          </Box>
                        </Stack>

                        {/* Actions */}
                        <Stack direction="row" spacing={1}>
                          <IconButton
                            size="small"
                            onClick={() => openEditDialog(a)}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                          <IconButton
                            size="small"
                            color="error"
                            disabled={deletingId === a.id}
                            onClick={() => requestDelete(a)}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Stack>
                      </Stack>
                    </CardContent>
                  </Card>
                );
              })
            )}
          </Box>
        </CardContent>
      </Card>

      {/* Create / Edit dialog */}
      <Dialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>
          {form.id ? "Edit announcement" : "New announcement"}
        </DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label="Title"
              size="small"
              value={form.title}
              onChange={(e) =>
                setForm((p) => ({ ...p, title: e.target.value }))
              }
              helperText="Shown as the main headline in the employee app."
            />

            <TextField
              label="Short summary"
              size="small"
              value={form.summary}
              onChange={(e) =>
                setForm((p) => ({ ...p, summary: e.target.value }))
              }
              helperText="Optional teaser text for list cards (1–2 lines)."
            />

            <TextField
              label="Full message"
              size="small"
              multiline
              minRows={4}
              value={form.body}
              onChange={(e) =>
                setForm((p) => ({ ...p, body: e.target.value }))
              }
              helperText="Full content shown when employees open the announcement."
            />

            <Stack direction="row" spacing={2}>
              <TextField
                label="Announcement date"
                type="date"
                size="small"
                value={form.date}
                onChange={(e) =>
                  setForm((p) => ({ ...p, date: e.target.value }))
                }
                InputLabelProps={{ shrink: true }}
                sx={{ flex: 1 }}
              />
              <TextField
                label="Valid until"
                type="date"
                required
                size="small"
                value={form.validUntil}
                onChange={(e) =>
                  setForm((p) => ({ ...p, validUntil: e.target.value }))
                }
                InputLabelProps={{ shrink: true }}
                sx={{ flex: 1 }}
                error={validUntilError}
                helperText={
                  validUntilError
                    ? "Till date is required."
                    : "Employees will stop seeing this announcement after this date."
                }
              />
            </Stack>

            <Stack direction="row" spacing={2}>
              <TextField
                select
                label="Category"
                size="small"
                value={form.category}
                onChange={(e) =>
                  setForm((p) => ({ ...p, category: e.target.value }))
                }
                SelectProps={{ native: true }}
                sx={{ flex: 1 }}
              >
                <option value="HOLIDAY">Holiday</option>
                <option value="POLICY">Policy</option>
                <option value="EVENT">Event</option>
                <option value="HR">HR Update</option>
                <option value="SYSTEM">System</option>
                <option value="GENERAL">General</option>
              </TextField>

              {/* Status selector only when editing – new ones are always Draft */}
              {form.id && (
                <TextField
                  select
                  label="Status"
                  size="small"
                  value={form.status}
                  onChange={(e) =>
                    setForm((p) => ({ ...p, status: e.target.value }))
                  }
                  SelectProps={{ native: true }}
                  sx={{ flex: 1 }}
                >
                  <option value="PUBLISHED">Published</option>
                  <option value="DRAFT">Draft</option>
                </TextField>
              )}
            </Stack>

            {!form.id && (
              <Typography variant="caption" color="text.secondary">
                New announcements are saved as <strong>Draft</strong>. You can
                publish them later from the list.
              </Typography>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            disabled={
              saving || !form.title.trim() || !form.validUntil
            }
            onClick={handleSave}
          >
            {form.id ? "Save changes" : "Create draft"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete confirmation dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => {
          if (!deletingId) {
            setDeleteDialogOpen(false);
            setPendingDelete(null);
          }
        }}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Delete announcement</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" sx={{ mb: 1 }}>
            Are you sure you want to delete this announcement?
          </Typography>
          {pendingDelete && (
            <Typography
              variant="subtitle2"
              sx={{ fontWeight: 600, mt: 0.5 }}
            >
              “{pendingDelete.title || "Untitled announcement"}”
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              if (!deletingId) {
                setDeleteDialogOpen(false);
                setPendingDelete(null);
              }
            }}
          >
            Cancel
          </Button>
          <Button
            color="error"
            variant="contained"
            disabled={!!deletingId}
            onClick={handleConfirmDelete}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

/* ---------- small presentational component for top summary pills ---------- */

function SummaryPill({ label, count, color, bg }) {
  return (
    <Box
      sx={{
        display: "inline-flex",
        alignItems: "center",
        gap: 1,
        px: 1.75,
        py: 0.75,
        borderRadius: 999,
        bgcolor: bg,
      }}
    >
      <Box
        sx={{
          width: 18,
          height: 18,
          borderRadius: "50%",
          bgcolor: "#fff",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          fontSize: 11,
          fontWeight: 600,
          color,
        }}
      >
        {count}
      </Box>
      <Typography
        variant="body2"
        sx={{ fontWeight: 500, color: "text.primary", fontSize: 13 }}
      >
        {label}
      </Typography>
    </Box>
  );
}
